import { createRef, useEffect, useState } from 'react'
import { MdClose, MdLocationOn, MdSave } from 'react-icons/md'
import type { ServiceAddressSnapshot } from '@/lib/api/common/service-address'
import ServiceAddressSection, { ServiceAddress } from './service-address-section'

interface ChangeServiceAddressModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: (address: ServiceAddress) => void | Promise<void>
    loading?: boolean
    /** Snapshot gravado no atendimento — abre já selecionado no modal. */
    current?: ServiceAddressSnapshot | null
    /** Do atendimento — de onde vêm os endereços oferecidos. */
    partnerId: number | null
    professionalId: number | null
    hospitalId?: number | null
    /** "exame" | "procedimento" | "consulta" — usado nos textos. */
    entityLabel: string
}

/**
 * Converte o snapshot gravado (campos anuláveis, vindos da API) na escolha que o
 * seletor manipula. Sem rua nem cidade não há o que pré-selecionar — o
 * atendimento nasceu sem local, e o operador escolhe do zero.
 */
function toSelection(
    snapshot?: ServiceAddressSnapshot | null,
): ServiceAddress | null {
    if (!snapshot) return null
    if (snapshot.addressSource === 'UNCONFIRMED') {
        return { addressSource: 'UNCONFIRMED' }
    }
    if (!snapshot.street && !snapshot.city) return null
    return {
        street: snapshot.street || '',
        number: snapshot.number || '',
        complement: snapshot.complement || '',
        neighborhood: snapshot.neighborhood || '',
        city: snapshot.city || '',
        state: snapshot.state || '',
        zipCode: snapshot.zipCode || '',
        country: snapshot.country || 'BR',
        addressSource: snapshot.addressSource || 'CUSTOM',
    }
}

/**
 * Troca o LOCAL DE ATENDIMENTO de um atendimento que já existe.
 *
 * O local é confirmado uma única vez — no agendamento ou na aprovação do
 * orçamento — e depois disso não havia como mexer nele: a tela de edição não
 * mostra o campo, e o operador que precisava corrigir a unidade acabava
 * cancelando e recriando o atendimento. Aqui o mesmo seletor do agendamento
 * reaparece, já apontando para o endereço gravado.
 *
 * Salvar muda SÓ o local: cobrança, agenda e status ficam onde estão, e o
 * paciente não é avisado — o endereço novo vale dos próximos lembretes e
 * documentos em diante.
 */
export default function ChangeServiceAddressModal({
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    current,
    partnerId,
    professionalId,
    hospitalId,
    entityLabel,
}: ChangeServiceAddressModalProps) {
    const modalRef = createRef<HTMLDialogElement>()
    const [address, setAddress] = useState<ServiceAddress | null>(null)

    useEffect(() => {
        if (isOpen) {
            // Reabrir o modal recomeça do que está GRAVADO, nunca da escolha
            // abandonada na abertura anterior.
            setAddress(toSelection(current))
            modalRef.current?.showModal()
        } else {
            modalRef.current?.close()
            setAddress(null)
        }
        // `current` entra na dependência para o modal refletir o snapshot recém-salvo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, current])

    return (
        <dialog className="modal z-50" ref={modalRef} onClose={onClose}>
            <div className="modal-box !max-w-3xl !mx-4 !p-0">
                <div className="flex items-center justify-between p-4 border-b border-base-300">
                    <h2 className="text-lg font-bold">
                        Alterar endereço de atendimento
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-sm btn-ghost btn-square"
                        disabled={loading}
                    >
                        <MdClose className="text-lg" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <p className="text-base-content/70">
                        Escolha onde o paciente será atendido. A mudança vale só
                        para este {entityLabel}: cobrança, agenda e status não são
                        alterados, e o endereço novo passa a valer nos lembretes e
                        documentos seguintes.
                    </p>

                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <MdLocationOn className="text-xl text-secondary" />
                            <h3 className="font-semibold">Local de atendimento</h3>
                        </div>
                        <ServiceAddressSection
                            partnerId={partnerId}
                            professionalId={
                                professionalId ? String(professionalId) : ''
                            }
                            hospitalId={hospitalId ?? null}
                            value={address}
                            onChange={setAddress}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 p-4 border-t border-base-300">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-outline-light !rounded-xl"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => address && onConfirm(address)}
                        className="btn btn-primary gap-2 !rounded-xl"
                        disabled={loading || !address}
                        title={
                            address
                                ? 'Salvar o local de atendimento'
                                : 'Escolha o local de atendimento para salvar'
                        }
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        Salvar
                    </button>
                </div>
            </div>

            <form method="dialog" className="modal-backdrop">
                <button onClick={onClose}>close</button>
            </form>
        </dialog>
    )
}
